/**
 * Coach-facing copy for the Goal Score Studio authoring surfaces: the new
 * "Goals" section (Distance / Speed / Angle kind picker, target source,
 * the Angle depth-vs-safety fork), the Form-effect curve editor, and the
 * per-calibration-take "Goal measurements" table.
 *
 * Every string here answers what/why/how per
 * `docs/COACH_FACING_COPY_STANDARD.md` and avoids every denylisted term
 * (`frontend/src/test/coachCopyDenylist.ts`) — in particular, the word
 * "landmark" is itself denylisted in rendered text, so every string below
 * says "point" or "joint" instead. Strings marked "verbatim" are quoted
 * directly from the design docs (`docs/goal_score/H2_form_effect_curve.md`
 * §5, `docs/goal_score/I_angle_goal_v1.md` §7, `REPORT.md` §2.4/§6.1) —
 * do not paraphrase them; a reviewer cross-checks these against the design
 * doc text, not against "close enough."
 *
 * Vocabulary: "Goal" is reserved for the competitive target this file's
 * copy describes. The six existing checkpoint types are "Checks" — never
 * call them "Goals" in new copy added by this file.
 */

// ---------------------------------------------------------------------------
// The Goals section — empty state, kind picker (F §2 steps 1-5)
// ---------------------------------------------------------------------------

export const GOALS_SECTION_TITLE = 'Goals';
export const GOALS_SECTION_SUBTITLE =
  "Optional bonus targets on top of your Checks — they're what turns a score into something athletes can compete for.";

export const GOALS_EMPTY_TITLE = 'This template has no Goals yet.';
export const GOALS_EMPTY_WHY =
  "Goals are optional bonus targets on top of your Checks — they're what turns a score into something athletes can compete for.";
export const GOALS_EMPTY_HOW_BUTTON = 'Add a Goal';

export const GOAL_KIND_LABELS: Record<'distance' | 'speed' | 'angle', string> = {
  distance: 'Distance',
  speed: 'Speed',
  angle: 'Angle',
};

export const GOAL_KIND_DESCRIPTIONS: Record<'distance' | 'speed' | 'angle', string> = {
  distance: 'How far something moves',
  speed: 'How fast something moves',
  angle: 'How bent a joint gets',
};

export const GOAL_STEP_PICK_KIND_TITLE = 'What kind of Goal is this?';
export const GOAL_STEP_PICK_POINT_TITLE = 'What should we track?';
export const GOAL_STEP_PICK_PHASE_TITLE = 'Which part of the movement?';
export const GOAL_STEP_PICK_PHASE_WHY =
  "A Goal has to mean the same thing for every athlete who tries it, so it needs one part of the movement to measure, not the whole clip.";
export const GOAL_STEP_PICK_PHASE_HOW = 'Pick the part of the movement this Goal belongs to.';

export const GOAL_STEP_WORTH_TITLE = 'How much is it worth?';
export const GOAL_STEP_WORTH_WHY =
  'Athletes earn these points on top of their score when they meet or beat this target.';
export const GOAL_STEP_WORTH_HOW = 'Set how many bonus points this is worth.';

export const GOAL_VALIDATION_NEEDS_KIND = 'Pick a kind of Goal to continue.';
export const GOAL_VALIDATION_NEEDS_POINT = 'Pick what to track before saving this Goal.';
export const GOAL_VALIDATION_NEEDS_PHASE = 'Pick a part of the movement before saving this Goal.';
export const GOAL_VALIDATION_NEEDS_TARGET = 'Set a target before saving this Goal.';

// Target-source A/B bar (reused idiom across Distance/Speed/Angle, F §2 step 4).
export const GOAL_SOURCE_DEMO_LABEL = 'Use what you demonstrated';
export const GOAL_SOURCE_DEMO_WHY = 'This becomes the target every athlete is compared against.';
export const GOAL_SOURCE_TYPED_LABEL = "I'll type the target myself";

export function goalSourceDemoMeasuringLine(): string {
  return 'Measuring your demo…';
}

export const GOAL_SOURCE_ERROR =
  "We couldn't measure your demo just now. Try again in a moment, or type the number yourself.";

export const GOAL_ABSTAIN_TITLE = "We can't measure this from your demo clip.";
export const GOAL_ABSTAIN_HOW = 'Pick a different point, or type the target yourself.';

export const GOAL_TOLERANCE_FLOOR_STATEMENT =
  "We always allow a small amount of slack, so a tiny camera difference doesn't cost a point.";

// ---------------------------------------------------------------------------
// Speed — the "your demo might be slow" prompt (B §5.5 / REPORT §3.2 — a
// named product requirement: the Speed authoring flow must warn, unlike
// Distance's blanket "use my demo" default, that the coach's own demo speed
// may be slower than their best athletes').
// ---------------------------------------------------------------------------

export const GOAL_SPEED_DEMO_CAUTION_TITLE = 'Your demo might be slower than your best athletes.';
export const GOAL_SPEED_DEMO_CAUTION_WHY =
  "If your own demo isn't your fastest, using it as the bar could make it too easy to beat.";
export const GOAL_SPEED_DEMO_CAUTION_HOW =
  'Use your demo if it fairly represents the speed you want, or type a faster target yourself.';

// ---------------------------------------------------------------------------
// Angle — depth-vs-safety-ceiling fork (I §7, REPORT §3.3/§6.1 — verbatim,
// shown directly after the direction chip whenever a coach picks "at most
// this bent"). This is the one authoring step that is NOT a copy nit: a
// safety ceiling that silently becomes a bonus-eligible depth challenge is
// a score-and-safety gap.
// ---------------------------------------------------------------------------

export const GOAL_ANGLE_FORK_TITLE = 'Is this a target to beat, or a limit never to cross?';
export const GOAL_ANGLE_FORK_WHY =
  'A target to beat earns bonus points the further under it the athlete gets. A limit is there to catch a mistake — it should never give extra points for just staying safely under it.';
export const GOAL_ANGLE_FORK_HOW =
  "Pick 'Target to beat' for something like a deep squat. Pick 'Limit' to keep this as a regular check instead of a Goal — crossing a limit should cost marks, not just miss a bonus.";

export const GOAL_ANGLE_FORK_OPTION_TARGET = 'Target to beat';
export const GOAL_ANGLE_FORK_OPTION_LIMIT = 'Limit';

// The "turn this existing Angle Check into a Goal" in-place flow (I §7) is NOT
// wired in PR-7b — tracked in backlog/goal-authoring-turn-angle-check-into-goal.md.
// Its copy was removed rather than left as dead exports (PR-7b red-team finding 1).

// Results-side copy (F §3 / I §7) — included here so the Studio preview and
// the eventual results surface share one source, not two independently
// drifting strings for the same idea.
export const GOAL_RESULT_MET_HOW =
  'Nothing to do — this still counts as a normal check, and you can try again next time.';
export const GOAL_RESULT_NOT_MET_WHY = 'The bonus is for beating the target, not just meeting it.';

// ---------------------------------------------------------------------------
// Form-effect curve editor (H2 §5, verbatim strings — REPORT.md §2.4/§6.1).
// ---------------------------------------------------------------------------

export const CURVE_CARD_TITLE = 'How does Form affect your Goal points?';

export const CURVE_ADD_POINT_LABEL = 'Add a point';
export const CURVE_ADD_POINT_LIMIT_NOTE = 'You can set up to 10 points.';
export const CURVE_REMOVE_POINT_FLOOR_NOTE = 'You need at least 2 points.';

export const CURVE_MAX_MULTIPLIER_LABEL = 'Maximum multiplier';
export const CURVE_MAX_MULTIPLIER_NOTE =
  'The highest bonus multiplier allowed on this template — you can go up to 5.0.';

export function curveMaxMultiplierNoNewPointNote(currentTopAnchorMultiplier: number): string {
  return (
    "Raising this alone won't change anything yet — add a point near the top of the Form " +
    `range if you want your best performers to get more than ${currentTopAnchorMultiplier}x your current top point.`
  );
}

export const CURVE_NEUTRAL_PRESET_LABEL = 'Use the standard setting (Form doesn’t change the bonus)';

export function curveWorkedPreviewSentence(form: number | null, multiplier: number | null): string {
  if (form === null || multiplier === null) {
    return 'With this curve, we’ll show how a Form score multiplies Goal points once we can measure one.';
  }
  return `With this curve, a Form of ${form} multiplies Goal points by ${multiplier.toFixed(2)}×.`;
}

export const CURVE_NEUTRAL_WORKED_PREVIEW_SUFFIX =
  '— every Form score does, until you edit the points above.';

export function curveTopQuartileLimitationNote(topAnchorForm: number, topAnchorMultiplier: number): string {
  return (
    `With just these three points, every Form above ${topAnchorForm} multiplies by the same ` +
    `${topAnchorMultiplier.toFixed(1)}× — add a fourth point above ${topAnchorForm} if you want to reward ` +
    "your very best performers more than that, not just 'above the pack.'"
  );
}

// Validation messages — verbatim, plain language (H2 §5 item 6 / REPORT §2.4).
// No "monotone," "anchor," or "strictly increasing" anywhere a coach reads.
export const CURVE_ERROR_FORM_NOT_INCREASING = 'Form values must increase from left to right.';
export const CURVE_ERROR_MULTIPLIER_DECREASING =
  'Multipliers can never go down as Form goes up — each one must be the same as or higher than the one before it.';
export const CURVE_ERROR_MULTIPLIER_BELOW_ZERO = 'The lowest a multiplier can be is 0.';
export function curveErrorAboveMaxMultiplier(maxMultiplier: number): string {
  return `No point on your curve can be higher than your maximum multiplier (${maxMultiplier}).`;
}
export const CURVE_ERROR_POINT_COUNT = 'You can set between 2 and 10 points.';

export const CURVE_EMPTY_ROW_NOTE = 'Add a Form value and a multiplier to finish this point, or remove it.';

// ---------------------------------------------------------------------------
// Per-calibration-take "Goal measurements" table (F §5, mockup 04).
// ---------------------------------------------------------------------------

export const TAKE_GOALS_PANEL_TITLE = 'Goal measurements';
export const TAKE_GOALS_MEASURE_BUTTON = 'Measure goals now';
export const TAKE_GOALS_MEASURING = 'Measuring…';
export const TAKE_GOALS_EMPTY_NOTE = 'This template has no Goals yet — add one in the Goals section above.';
export const TAKE_GOALS_ERROR = "We couldn't measure these goals just now. Try again in a moment.";

export const TAKE_GOAL_STATUS_LABELS: Record<'exceeded' | 'met' | 'missed' | 'abstained', string> = {
  exceeded: 'Exceeded',
  met: 'Met',
  missed: 'Missed',
  abstained: 'Could not measure',
};

export const TAKE_GOAL_ABSTAIN_HOW = "Nothing to do — this won't affect the take's grade.";

export function takeGoalMeasuredLine(
  measured: number | null,
  target: number | null,
  unitSuffix: string,
): string {
  if (measured === null || target === null) return '—';
  return `${measured}${unitSuffix} — target ${target}${unitSuffix}`;
}
